/**
 * CallReplayPage — /calls/:callSlug/replay/:date
 *
 * The replay of one call date, hosted on Bunny (mct_31dc54a4-dc9). Michael,
 * 28 Oct 2026 (mca_efa4d80eed8d): Builds and TSB Live replays are free to
 * registrants for a week after each session, then they belong to Member Plus.
 * So the page plays the recording while the call's free window is open, with
 * a Member Plus section under the video; once the window closes the Member
 * Plus section is the whole page.
 *
 * The replay email links here through one of our smart links. The worker
 * (GET /api/public/calls/:slug/replay/:date, calls-replay-page.ts) decides
 * open or closed and hands over a signed, expiring Bunny iframe only while
 * open — this page never sees a raw video address.
 *
 * Members (mct_1a040982-7a9): when member_gate is on, the people the call's
 * replay goes to (Clients members on the Clients coaching call, Member Plus and
 * Alumni on Builds) type the email they get the call emails at, and the worker
 * (POST …/access) checks it against the call's own list before it signs the
 * player. Nothing about the email goes in the URL.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { useParams, Link } from 'react-router-dom';
import TakerShell from './components/TakerShell';
import { useBrandGtm } from './lib/useBrandGtm';

const API_BASE = (import.meta as unknown as { env: Record<string, string> }).env?.VITE_API_BASE_URL ?? '';

interface ReplayData {
  state: 'open' | 'closed' | 'not-ready';
  call: { slug: string; title: string; brand: string };
  session: { date: string; start_utc: string };
  free_until: string | null;
  /** The call's free window in days, counted from release; null = members only. */
  window_days?: number | null;
  video: { iframe_url: string; duration_seconds: number | null } | null;
  member_plus: { url: string | null; url_annual: string | null };
  /** Our smart link to the call's recordings in the community, for members. */
  recordings_url?: string | null;
  member_gate?: boolean;
  /** Handout links for this date, set by the operator (smart links only). */
  resources?: Array<{ label: string; url: string }>;
}

type Video = NonNullable<ReplayData['video']>;

type PageState =
  | { phase: 'loading' }
  | { phase: 'error' }
  | { phase: 'ready'; data: ReplayData };

function sessionDay(startUtc: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Toronto', weekday: 'long', month: 'long', day: 'numeric',
  }).format(new Date(startUtc));
}

function closesOn(iso: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Toronto', weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
  }).format(new Date(iso));
}

export default function CallReplayPage() {
  const { callSlug, date } = useParams<{ callSlug: string; date: string }>();
  const [state, setState] = useState<PageState>({ phase: 'loading' });
  useBrandGtm();

  useEffect(() => {
    if (!callSlug || !date) { setState({ phase: 'error' }); return; }
    fetch(`${API_BASE}/api/public/calls/${encodeURIComponent(callSlug)}/replay/${encodeURIComponent(date)}`)
      .then(async (r) => {
        const d = await r.json().catch(() => null) as (ReplayData & { ok?: boolean }) | null;
        if (!r.ok || !d?.ok) { setState({ phase: 'error' }); return; }
        setState({ phase: 'ready', data: d });
      })
      .catch(() => setState({ phase: 'error' }));
  }, [callSlug, date]);

  const callPath = callSlug ? `/calls/${encodeURIComponent(callSlug)}` : '/';

  return (
    <TakerShell>
      <div style={{ maxWidth: 880, margin: '0 auto', padding: '40px 20px 64px', fontFamily: "'Poppins', sans-serif", color: '#0A0A2E' }}>
        {state.phase === 'loading' && (
          <div style={{ minHeight: '40vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280', fontSize: 14 }}>
            One moment…
          </div>
        )}

        {state.phase === 'error' && (
          <div style={{ textAlign: 'center', padding: '64px 0' }}>
            <p style={{ fontSize: 15, color: '#6b7280', marginBottom: 18 }}>We couldn't find that replay.</p>
            <Link to={callPath} style={{ color: '#F05A28', fontWeight: 600, fontSize: 14, textDecoration: 'none' }}>
              Go to the call page
            </Link>
          </div>
        )}

        {state.phase === 'ready' && <Ready data={state.data} callPath={callPath} callSlug={callSlug!} date={date!} />}
      </div>
    </TakerShell>
  );
}

function Player({ video, title }: { video: Video; title: string }) {
  return (
    <div style={{ position: 'relative', paddingTop: '56.25%', borderRadius: 12, overflow: 'hidden', background: '#0A0A2E', boxShadow: '0 10px 30px rgba(10,10,46,0.18)' }}>
      <iframe
        src={video.iframe_url}
        title={`${title} replay`}
        loading="lazy"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
}

function Ready({ data, callPath, callSlug, date }: { data: ReplayData; callPath: string; callSlug: string; date: string }) {
  const day = sessionDay(data.session.start_utc);
  const [unlocked, setUnlocked] = useState<{ video: Video; firstName: string | null } | null>(null);
  const membersOnly = data.window_days === null || (data.window_days === undefined && data.free_until === null);
  const showGate = !!data.member_gate && data.state !== 'open' && !unlocked;
  return (
    <>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#F05A28', letterSpacing: 0.6, textTransform: 'uppercase', marginBottom: 10 }}>
        Replay · {day}
      </div>
      <h1 style={{ fontFamily: "'Fraunces', serif", fontSize: 34, fontWeight: 800, lineHeight: 1.15, margin: '0 0 14px' }}>
        {data.call.title}
      </h1>

      {data.resources && data.resources.length > 0 && (
        <div style={{ margin: '0 0 22px', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {data.resources.map((r) => (
            <a key={r.url} href={r.url} style={{
              display: 'inline-block', background: '#0A0A2E', color: '#fff', padding: '11px 22px', borderRadius: 8,
              fontSize: 14, fontWeight: 700, textDecoration: 'none',
            }}>
              {r.label}
            </a>
          ))}
        </div>
      )}

      {data.state === 'open' && data.video && (
        <>
          {data.free_until && (
            <p style={{ fontSize: 15, color: '#4b5563', lineHeight: 1.6, margin: '0 0 22px' }}>
              This replay is free to watch until <strong>{closesOn(data.free_until)}</strong>. After that it moves into the replay vault, part of Member Plus.
            </p>
          )}
          {data.free_until && <FreeWeekCountdown until={data.free_until} />}
          <Player video={data.video} title={data.call.title} />
        </>
      )}

      {unlocked && (
        <>
          <p style={{ fontSize: 15, color: '#4b5563', lineHeight: 1.6, margin: '0 0 22px' }}>
            {unlocked.firstName ? `Thanks, ${unlocked.firstName}. ` : 'Thanks. '}Here's the recording from {day}.
          </p>
          <Player video={unlocked.video} title={data.call.title} />
        </>
      )}

      {showGate && membersOnly && (
        <p style={{ fontSize: 16, color: '#4b5563', lineHeight: 1.7, margin: '0 0 8px' }}>
          This recording is for the members of this call. Enter the email you get the call emails at, and it will play right here.
        </p>
      )}

      {data.state === 'closed' && !unlocked && !(showGate && membersOnly) && (
        <p style={{ fontSize: 16, color: '#4b5563', lineHeight: 1.7, margin: '0 0 8px' }}>
          The free week for this replay has ended, and the recording now lives in the replay vault for Member Plus.
          If you're already a member, you'll find it with the other call recordings in the community.
        </p>
      )}

      {data.state === 'not-ready' && (
        <p style={{ fontSize: 16, color: '#4b5563', lineHeight: 1.7, margin: '0 0 8px' }}>
          The recording from {day} is on its way. We'll email you as soon as it's ready{data.window_days ? `, and it's yours free for ${data.window_days} ${data.window_days === 1 ? 'day' : 'days'} from then` : ''}.
        </p>
      )}

      {showGate && (
        <MemberUnlock callSlug={callSlug} date={date} prompt={membersOnly ? null : 'Already a member? Enter the email you get the call emails at to watch it here.'}
          onUnlock={(video, firstName) => setUnlocked({ video, firstName })} />
      )}

      {!unlocked && <MemberPlus links={data.member_plus} closed={data.state === 'closed'} recordingsUrl={data.recordings_url ?? null} />}

      <p style={{ textAlign: 'center', marginTop: 32, fontSize: 14 }}>
        <Link to={callPath} style={{ color: '#F05A28', fontWeight: 600, textDecoration: 'none' }}>
          See when the next {data.call.title} is
        </Link>
      </p>
    </>
  );
}

function MemberPlus({ links, closed, recordingsUrl }: { links: ReplayData['member_plus']; closed: boolean; recordingsUrl: string | null }) {
  if (!links.url) return null;
  return (
    <section style={{
      marginTop: 40, padding: '32px 28px', borderRadius: 14, background: '#FFF5EF', border: '1px solid #FFE4D6', textAlign: 'center',
    }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#F05A28', letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 10 }}>
        Member Plus
      </div>
      <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 26, fontWeight: 800, margin: '0 0 12px' }}>
        {closed ? 'Watch this one, and every one before it' : 'Keep every replay, not just this week’s'}
      </h2>
      <p style={{ fontSize: 15, color: '#4b5563', lineHeight: 1.7, maxWidth: 560, margin: '0 auto 18px' }}>
        Member Plus opens the replay vault: every recorded call, there whenever you need it.
        You also get a seat on the live calls each week, where you can bring your own questions.
      </p>
      <a href={links.url} className="cta-btn" style={{
        display: 'inline-block', background: '#F05A28', color: '#fff', padding: '13px 32px', borderRadius: 8,
        fontSize: 15, fontWeight: 700, textDecoration: 'none', letterSpacing: 0.2,
      }}>
        Join Member Plus
      </a>
      {links.url_annual && (
        <p style={{ fontSize: 13, color: '#6b7280', margin: '14px 0 0' }}>
          Prefer to pay once a year?{' '}
          <a href={links.url_annual} style={{ color: '#F05A28', fontWeight: 600 }}>See the yearly plan</a>.
        </p>
      )}
      {recordingsUrl && (
        <p style={{ fontSize: 14, color: '#4b5563', margin: '20px 0 0', paddingTop: 18, borderTop: '1px solid #FFE4D6' }}>
          Already a member?{' '}
          <a href={recordingsUrl} style={{ color: '#F05A28', fontWeight: 600 }}>Access your recordings</a>.
        </p>
      )}
    </section>
  );
}

function MemberUnlock({ callSlug, date, prompt, onUnlock }: {
  callSlug: string; date: string; prompt: string | null;
  onUnlock: (video: Video, firstName: string | null) => void;
}) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy || !email.trim()) return;
    setBusy(true);
    setMessage(null);
    try {
      const r = await fetch(`${API_BASE}/api/public/calls/${encodeURIComponent(callSlug)}/replay/${encodeURIComponent(date)}/access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const d = await r.json().catch(() => null) as { ok?: boolean; reason?: string; first_name?: string | null; video?: Video } | null;
      if (d?.ok && d.video) { onUnlock(d.video, d.first_name ?? null); return; }
      setMessage(d?.reason === 'not-ready'
        ? 'The recording isn’t ready to play just yet. Please try again a little later.'
        : 'We couldn’t find that email on this call’s member list. Try the address your call emails come to, or reply to any of them and we’ll sort it out.');
    } catch {
      setMessage('Something went wrong on our side. Please try again in a moment.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} style={{ margin: '22px 0 8px', padding: '24px', borderRadius: 12, border: '1px solid #e5e7eb', background: '#fff' }}>
      {prompt && <p style={{ fontSize: 15, color: '#4b5563', lineHeight: 1.6, margin: '0 0 14px' }}>{prompt}</p>}
      <label htmlFor="replay-email" style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Your email</label>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <input
          id="replay-email"
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@yourfirm.com"
          style={{ flex: '1 1 240px', minWidth: 0, padding: '12px 14px', fontSize: 15, borderRadius: 8, border: '1px solid #d1d5db', fontFamily: 'inherit' }}
        />
        <button type="submit" disabled={busy} className="cta-btn" style={{
          background: '#F05A28', color: '#fff', padding: '12px 26px', borderRadius: 8, border: 0,
          fontSize: 15, fontWeight: 700, cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit',
        }}>
          {busy ? 'Checking…' : 'Watch the replay'}
        </button>
      </div>
      {message && <p role="alert" style={{ fontSize: 14, color: '#b45309', lineHeight: 1.6, margin: '12px 0 0' }}>{message}</p>}
    </form>
  );
}

/** Counts down to the end of the free week (free_until, the same moment the
 *  worker closes the page). At zero it reloads, so the page moves to its
 *  closed, Member Plus-only state from the worker's answer, not the clock. */
function FreeWeekCountdown({ until }: { until: string }) {
  const end = Date.parse(until);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const left = Math.max(0, end - now);
  useEffect(() => {
    if (Number.isFinite(end) && left === 0) {
      const t = setTimeout(() => window.location.reload(), 1500);
      return () => clearTimeout(t);
    }
  }, [left, end]);
  if (!Number.isFinite(end)) return null;
  const s = Math.floor(left / 1000);
  const parts: Array<[number, string]> = [
    [Math.floor(s / 86400), 'days'],
    [Math.floor((s % 86400) / 3600), 'hours'],
    [Math.floor((s % 3600) / 60), 'minutes'],
    [s % 60, 'seconds'],
  ];
  return (
    <div role="timer" aria-label="Time left to watch free" style={{
      display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap',
      margin: '0 0 22px', padding: '14px 18px', borderRadius: 12, background: '#0A0A2E', color: '#fff',
    }}>
      <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: '#FFB08F' }}>
        {left === 0 ? 'The free week has ended' : 'Free to watch for'}
      </div>
      {left > 0 && (
        <div style={{ display: 'flex', gap: 14 }}>
          {parts.map(([n, label]) => (
            <div key={label} style={{ textAlign: 'center', minWidth: 44 }}>
              <div style={{ fontFamily: "'Fraunces', serif", fontSize: 26, fontWeight: 800, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                {String(n).padStart(2, '0')}
              </div>
              <div style={{ fontSize: 11, color: '#c7c9e0', marginTop: 4 }}>{n === 1 ? label.slice(0, -1) : label}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
